import { useSelector } from 'react-redux'

import PricePanelAmount from '@components/UI/PricePanel/Amount'
import { SupportedSubmissionTypes } from '@enums'
import { IApplicationState } from '@store'
import { ILicenseOption, selectCampaignMinimumLicenseOption } from '@store/campaigns'
import {
  ITransactedLicense,
  selectMediaObjectType,
  selectTransactedLicense,
} from '@store/mediaObjects'
import { ISubmission, selectPriceBeforePreApproval, selectSubmission } from '@store/submissions'

interface IProps {
  showLicensePrice: boolean
  showLicenseOptionsPrice: boolean
  submissionId: number
  campaignId: number
  submissionType: SupportedSubmissionTypes
  mediaObjectId: number
}

const getTaxDisplayName = (
  isContentSubmission: boolean,
  showLicensePrice: boolean,
  minimumLicenseOption: ILicenseOption,
  transactedLicense: ITransactedLicense,
  submission: ISubmission,
) => {
  if (isContentSubmission) {
    return minimumLicenseOption.tax_display_name
  }
  if (showLicensePrice) {
    return transactedLicense.tax_display_name
  }
  return submission.tax_display_name
}

const SubmissionCardPricePanelAmount: React.FC<IProps> = ({
  submissionType,
  showLicensePrice,
  showLicenseOptionsPrice,
  submissionId,
  mediaObjectId,
  campaignId,
}) => {
  const {
    hasPriceChangeSincePreApproval,
    minimumLicenseOption,
    submission,
    transactedLicense,
  } = useSelector((state: IApplicationState) => {
    const submission = selectSubmission(state, submissionId)
    const mediaObjectMediaType = selectMediaObjectType(state, mediaObjectId)

    const mediaType = submission?.final_media_type ?? mediaObjectMediaType

    return {
      submission: selectSubmission(state, submissionId),
      hasPriceChangeSincePreApproval: Boolean(
        selectPriceBeforePreApproval(state, submissionId, campaignId),
      ),
      minimumLicenseOption: selectCampaignMinimumLicenseOption(
        state,
        campaignId,
        mediaType,
      ) as ILicenseOption,
      transactedLicense: selectTransactedLicense(state, mediaObjectId),
    }
  })
  const {
    total_amount_before_tax_currency,
    amount_currency,
    total_amount_before_tax_cents,
    is_web3,
  } = submission

  const isContentSubmission = submissionType === SupportedSubmissionTypes.ContentSubmission

  const taxDisplayName = is_web3
    ? ''
    : getTaxDisplayName(
        isContentSubmission,
        showLicensePrice,
        minimumLicenseOption,
        transactedLicense,
        submission,
      )

  if (showLicenseOptionsPrice) {
    return (
      <PricePanelAmount
        showFrom
        price={minimumLicenseOption.total_amount_before_tax_cents}
        currency={minimumLicenseOption.total_amount_before_tax_currency || amount_currency}
        taxDisplayName={taxDisplayName}
        isInformationAvailable={!is_web3}
      />
    )
  }

  if (showLicensePrice) {
    return (
      <PricePanelAmount
        price={transactedLicense.total_amount_before_tax_cents || 0}
        currency={transactedLicense.total_amount_before_tax_currency || amount_currency}
        taxDisplayName={taxDisplayName}
        isInformationAvailable={!is_web3}
      />
    )
  }

  return (
    <PricePanelAmount
      price={total_amount_before_tax_cents || 0}
      currency={total_amount_before_tax_currency || amount_currency}
      taxDisplayName={taxDisplayName}
      hasPriceChanged={hasPriceChangeSincePreApproval}
      isInformationAvailable={!is_web3}
    />
  )
}

export default SubmissionCardPricePanelAmount
